import {
  IFormUpdater,
  IFormUpdaters,
  IFormUtils,
  TControlName,
  TUpdaterResult,
} from '@infotech-soe/metaschema';
import defaultsDeep from 'lodash/defaultsDeep';

import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import CategoryViewModel from '../../category/view-model/CategoryViewModel';
import IContextService from '../../context/IContextService';
import { DatasetCurrentState } from '../../dataset/view-model/DatasetCurrentState';
import UniqueId from '../../uid/UniqueId';
import FormApi from '../api/FormApi';
import {
  FormControlViewModel,
  FormControlViewModelType,
  SelectFormControlEntry,
} from '../view-model/FormControlViewModel';
import {
  CategoryFormSource,
  ConditionFormSource,
  FormSource,
  FormSourceType,
} from '../view-model/FormSource';
import { FormControls, FormUpdateViewModel } from '../view-model/FormViewModel';

import updateCheckboxGroup from './inputs/checkGroup';
import updateDate from './inputs/date';

export const mergeWithUpdaterResult = async (
  controls: FormControls,
  args: Record<TControlName, any>,
  updaterResult: TUpdaterResult
): Promise<FormControls> => {
  const apiProvider = new MerpsApiProvider();
  const updatedControls: FormControls = {};
  for (const [updaterResFieldName, updaterResFieldVal] of Object.entries(
    updaterResult
  )) {
    const ctrl = controls[updaterResFieldName];
    if (!ctrl) {
      throw new Error(`Unknown control "${updaterResFieldName}"`);
    }
    if (ctrl.type === FormControlViewModelType.TREE) {
      continue;
    }
    const updatedCtrl: FormControlViewModel = defaultsDeep(
      {},
      updaterResFieldVal,
      ctrl
    );

    if (updatedCtrl.type === FormControlViewModelType.SELECT_INPUT) {
      if (updaterResFieldVal.options) {
        updatedCtrl.options =
          updaterResFieldVal.options as SelectFormControlEntry[];
      }
    }
    if (updatedCtrl.type === FormControlViewModelType.DICTIONARY_INPUT) {
      if (updaterResFieldVal.inParams) {
        updatedCtrl.inParams = updaterResFieldVal.inParams;
      }
      if (updaterResFieldVal.outParams) {
        updatedCtrl.outParams = updaterResFieldVal.outParams;
      }
    }
    if (
      (updatedCtrl.type === FormControlViewModelType.IMAGE ||
        updatedCtrl.type === FormControlViewModelType.RICHTEXT) &&
      updaterResFieldVal.value
    ) {
      let val: ArrayBuffer | undefined = undefined;
      if (updaterResFieldVal.value.id || updaterResFieldVal.value.streamId) {
        val = await apiProvider.streams.read(
          updaterResFieldVal.value.id || updaterResFieldVal.value.streamId
        );
      }
      if (val !== undefined) {
        if (updatedCtrl.type === FormControlViewModelType.RICHTEXT) {
          const v = (updatedCtrl.value = Buffer.from(val).toString('utf-8'));
          updatedCtrl.value = v;
          args[updaterResFieldName] = v;
        } else {
          updatedCtrl.value = val;
        }
        delete args[updaterResFieldName];
      } else {
        args[updaterResFieldName] = updaterResFieldVal.value;
      }
    }

    if (
      updatedCtrl.hasValue &&
      updatedCtrl.type !== FormControlViewModelType.IMAGE
    ) {
      if (updatedCtrl.type === FormControlViewModelType.CHECKBOX_GROUP) {
        updatedCtrl.value = updaterResFieldVal.value;
        args[updaterResFieldName] = updateCheckboxGroup(
          updatedCtrl,
          updaterResFieldVal.value
        );
      } else if (
        updatedCtrl.type === FormControlViewModelType.DATE ||
        updatedCtrl.type === FormControlViewModelType.DICT_DATE
      ) {
        updatedCtrl.value = updateDate(updatedCtrl.value, updatedCtrl);
        args[updaterResFieldName] = updatedCtrl.value;
      } else if (
        updatedCtrl.type === FormControlViewModelType.DATASET &&
        updaterResFieldVal.value
      ) {
        updatedCtrl.value = updaterResFieldVal.value;
        args[updaterResFieldName] = updaterResFieldVal.value;
      } else {
        args[updaterResFieldName] = updatedCtrl.value;
      }
    }
    updatedControls[ctrl.name] = updatedCtrl;
  }
  return updatedControls;
};

export const executeUpdater = async (
  ctxService: IContextService,
  category: CategoryViewModel,
  datasetsState: Record<UniqueId, DatasetCurrentState>,
  formSource: CategoryFormSource | ConditionFormSource,
  apiVars: Map<string, any>,
  singleUpdater: IFormUpdater,
  values: Record<TControlName, any>,
  controlName: TControlName,
  utils: IFormUtils,
  controls: FormControls,
  args: Record<TControlName, any>
): Promise<FormUpdateViewModel | void> => {
  const context = await ctxService.getContext(
    category,
    datasetsState,
    formSource.dataset
  );

  const formApi = new FormApi(
    apiVars,
    formSource.type === FormSourceType.FORM && formSource.multiStep
      ? formSource.multiStep.prevState
      : {},
    formSource.application
  );

  const updaterResult = await singleUpdater.func(
    formApi,
    context.toClientApiContext(),
    values,
    controlName,
    utils
  );

  if (updaterResult) {
    const updatedControls = await mergeWithUpdaterResult(
      controls,
      args,
      updaterResult
    );

    if (Object.keys(updatedControls).length > 0) {
      return {
        controls: updatedControls,
        args,
        apiVars: formApi.getVars(),
        triggered: true,
      };
    }
  }
};

const updateWithSchemaUpdater = async (
  ctx: IContextService,
  updater: IFormUpdaters,
  utils: IFormUtils,
  source: FormSource,
  value: any,
  controlName: TControlName,
  control: FormControlViewModel,
  controls: FormControls,
  sourceArgs: Record<TControlName, any>,
  apiVars: Map<string, any>,
  category: CategoryViewModel,
  datasetsState: Record<UniqueId, DatasetCurrentState>
): Promise<FormUpdateViewModel | void> => {
  const updatedControls: FormControls = {};
  const args: Record<TControlName, any> = {};

  if (source.type === FormSourceType.PREDEFINED) {
    return;
  }

  for (const key of Object.keys(updater)) {
    if (updater[key].watch?.includes(control.name)) {
      const values = { ...sourceArgs, [controlName]: value };

      if (
        control.type === FormControlViewModelType.DICTIONARY_INPUT &&
        control.outParams &&
        typeof value === 'object'
      ) {
        Object.entries(control.outParams).forEach(
          ([outParamName, outParamValue]) => {
            (Array.isArray(outParamValue)
              ? outParamValue
              : [outParamValue]
            ).forEach(outParamControl => {
              if (outParamControl === controlName) {
                updatedControls[controlName] = {
                  ...control,
                  value: value[outParamName],
                };
                values[controlName] = value[outParamName];
              } else {
                args[outParamControl] = value[outParamName];
              }
            });
          }
        );
      }

      const updaterResult = await executeUpdater(
        ctx,
        category,
        datasetsState,
        source,
        apiVars,
        updater[key],
        values,
        controlName,
        utils,
        controls,
        args
      );

      if (updaterResult) {
        return updaterResult;
      }
    }
  }
};

export default updateWithSchemaUpdater;
